/**
 * Unified Game Server Service
 *
 * This service integrates with the unified Node.js server that now includes
 * all game server functionality previously handled by the separate FastAPI server.
 *
 * Based on Node.js Server Documentation - Part 3: Integration & Testing Summary
 */

import { getAxiosInstance } from './api'
import type { AxiosInstance, AxiosResponse } from 'axios'
import type { User } from '../types/app'

// Admin billing overview (GET /game-servers/admin/billing-overview)
export interface AdminBillingServerRow {
  id: string
  serverName: string
  owner: { id?: string; username: string | null; email: string | null } | null
  monthlyCost: number
  status: string
  subscriptionStatus: string
  nextPaymentDue: string | null
  isOverdue: boolean
  daysUntilDue: number | null // negative = days overdue
  // Oldest unpaid invoice for this server (null = fully paid up).
  openInvoice: {
    invoiceNumber: string
    amount: number
    status: string
    dueDate: string
    daysOverdue: number
  } | null
}
export interface AdminBillingOverview {
  success: boolean
  generatedAt: string
  summary: {
    totalServers: number
    suspended: number
    overdue: number
    activeSubscriptions: number
    mrr: number // all billable servers (manual + autopay)
    autopayMrr: number // PayPal auto-pay slice only
  }
  servers: AdminBillingServerRow[]
}

// Game Server Types based on Node.js server models
interface GameServer {
  _id?: string
  id?: string | number
  name: string
  status: 'pending' | 'running' | 'stopped' | 'failed' | 'provisioning' | 'starting' | 'stopping' | 'restarting' | 'crashed' | 'deleting' | 'suspended'
  ip?: string
  port?: number
  game_type: string
  ram_gb: number
  max_players?: number
  current_players?: number
  player_count?: number
  // Live occupancy probed from the game server (UDP getstatus / rcon status)
  player_names?: string[]
  live_map?: string
  query_online?: boolean
  user_id: string
  created_at?: string
  updated_at?: string
  [key: string]: any
}

interface ServerData {
  name?: string
  server_name?: string
  game_type: string
  ram_gb?: number
  max_players?: number
  memory_gb?: number
  [key: string]: any
}

interface SystemMetrics {
  cpu_usage: number
  memory_usage: number
  disk_usage: number
  active_servers: number
  total_players: number
}

interface PricingInfo {
  monthly_price: number
  base_price: number
  ram_cost_per_gb: number
  ram_tiers: {
    '2GB': number
    '4GB': number
    '8GB': number
    '16GB': number
  }
  total_cost?: number
}

interface PaymentInitiation {
  order_id: string
  orderId: string
  approval_url: string
  approvalUrl: string
  total_cost: number
  totalCost: number
  message: string
}

interface PaymentCompletion {
  success?: boolean
  status?: 'success' | 'failed'
  message: string
  server?: GameServer
}

interface ServerCapacity {
  available_ports: number
  total_ports: number
  active_servers: number
  max_servers: number
}

class GameServerService {
  private api: AxiosInstance

  constructor() {
    this.api = getAxiosInstance()
  }

  /**
   * Get API pricing information
   */
  async getGameServerPricing(): Promise<PricingInfo> {
    const response: AxiosResponse<PricingInfo> = await this.api.get(
      '/game-servers/pricing',
    )
    return response.data
  }

  /**
   * Get server capacity information
   */
  async getGameServerCapacity(): Promise<ServerCapacity> {
    const response: AxiosResponse<ServerCapacity> = await this.api.get(
      '/game-servers/capacity',
    )
    return response.data
  }

  /**
   * Get user's game servers
   */
  // async getGameServers(): Promise<GameServer[]> {
  //   console.log('🔍 Fetching user game servers...')
  //   const response: AxiosResponse<GameServer[]> = await this.api.get(
  //     '/game-servers/my-servers',
  //   )
  //   console.log('📊 Raw API response:', response.data)
  //   console.log('📊 Response type:', typeof response.data)
  //   console.log('📊 Is array:', Array.isArray(response.data))

  //   // Ensure we always return an array
  //   const servers = Array.isArray(response.data) ? response.data : []
  //   console.log('✅ Processed servers:', servers)
  //   return servers
  // }

  /**
   * Get specific game server details
   */
  async getGameServer(serverId: string | number): Promise<GameServer> {
    const response: AxiosResponse<GameServer> = await this.api.get(
      `/game-servers/${serverId}`,
    )
    return response.data
  }

  /**
   * Get user's game servers
   */
  async getGameServers(): Promise<GameServer[]> {
    console.log('🔍 Fetching user game servers...')
    const response: AxiosResponse<{ success: boolean; servers: any[] }> =
      await this.api.get('/game-servers/my-servers')
    console.log('📊 Raw API response:', response.data)

    // The API returns an object with a 'servers' key.
    const rawServers = response.data?.servers || []

    // Map the incoming camelCase properties to the snake_case properties expected by the frontend.
    const processedServers: GameServer[] = rawServers.map((server: any) => ({
      ...server, // Spread existing properties that might match
      id: server.id,
      name: server.name,
      status: server.status,
      port: server.port,
      game_type: server.gameType, // Map gameType to game_type
      player_count: server.playerCount, // Map playerCount to player_count
      current_players: server.playerCount, // Also map to current_players for consistency
      // Live occupancy probed from the game server itself (names + map + up/down).
      player_names: server.live?.players || [],
      live_map: server.live?.map || undefined,
      query_online: server.live?.online,
      max_players: server.maxPlayers, // Map maxPlayers to max_players
      created_at: server.createdAt, // Map createdAt to created_at
      ram_gb: server.memoryGb, // Map memoryGb to ram_gb
      ip: server.serverIp, // Map serverIp to ip
      monthly_cost: server.monthlyCost, // Map monthlyCost
      userId: server.userId, // Pass through (may be populated object for admin)
    }))

    console.log('✅ Processed and mapped servers:', processedServers)
    return processedServers
  }

  /**
   * Create a new game server (now integrated with payment flow)
   * Note: This initiates the payment process instead of directly creating
   */
  async createGameServer(serverData: ServerData): Promise<GameServer> {
    // Transform data to match API expectations
    const apiData = {
      ...serverData,
      name: serverData.server_name || serverData.name || 'Unnamed Server',
      ram_gb: serverData.memory_gb || serverData.ram_gb || 2,
    }

    const response: AxiosResponse<GameServer> = await this.api.post(
      '/game-servers',
      apiData,
    )
    return response.data
  }

  /**
   * Update game server configuration
   */
  async updateGameServer(
    serverId: string | number,
    serverData: Partial<ServerData>,
  ): Promise<GameServer> {
    const response: AxiosResponse<GameServer> = await this.api.put(
      `/game-servers/${serverId}`,
      serverData,
    )
    return response.data
  }

  /**
   * Delete game server
   */
  async deleteGameServer(
    serverId: string | number,
  ): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(
      `/game-servers/${serverId}`,
    )
    return response.data
  }

  /**
   * Start game server
   */
  async startGameServer(
    serverId: string | number,
  ): Promise<{ message: string; status?: string }> {
    const response: AxiosResponse<{ message: string; status?: string }> =
      await this.api.put(`/game-servers/${serverId}/start`)
    return response.data
  }

  /**
   * Stop game server
   */
  async stopGameServer(
    serverId: string | number,
  ): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(
      `/game-servers/${serverId}/stop`,
    )
    return response.data
  }

  /**
   * Restart game server
   */
  async restartGameServer(
    serverId: string | number,
  ): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.put(
      `/game-servers/${serverId}/restart`,
    )
    return response.data
  }

  /**
   * Get real-time server status
   */
  async getGameServerStatus(serverId: string | number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      `/game-servers/${serverId}/status`,
    )
    return response.data
  }

  /**
   * Get server logs
   */
  async getGameServerLogs(
    serverId: string | number,
    lines: number = 100,
  ): Promise<{ logs: string[]; lines: number }> {
    const response: AxiosResponse<{ logs: string[]; lines: number }> =
      await this.api.get(`/game-servers/${serverId}/logs?lines=${lines}`)
    return response.data
  }

  // =============================================================================
  // PAYMENT FLOW METHODS (New unified payment system)
  // =============================================================================

  /**
   * Initiate server purchase - starts PayPal payment flow
   */
  async initiateServerPurchase(
    serverConfig: ServerData,
  ): Promise<PaymentInitiation> {
    console.log('🚀 Initiating server purchase:', serverConfig)
    const response: AxiosResponse<PaymentInitiation> = await this.api.post(
      // '/payment-flow/create-server-directly',
      // '/server-purchase/purchase/initialize',
      '/payment-flow/initiate-server-purchase',
      serverConfig,
    )
    console.log('💳 Payment initiation response:', response.data)
    return response.data
  }

  /**
   * Initiate a NEW server purchase as a recurring PayPal subscription (auto-pay).
   * Server is provisioned by the webhook handler when BILLING.SUBSCRIPTION.ACTIVATED arrives.
   */
  async initiateServerSubscription(
    serverConfig: ServerData,
  ): Promise<PaymentInitiation & { subscriptionId: string; cycleAmount: number; durationMonths: 1 | 3 | 6 }> {
    console.log('🚀 Initiating auto-pay server subscription:', serverConfig)
    const response = await this.api.post(
      '/payment-flow/initiate-server-subscription',
      serverConfig,
    )
    console.log('💳 Subscription initiation response:', response.data)
    return response.data
  }

  /**
   * Complete server purchase - captures payment and creates server
   */
  async completeServerPurchase(
    orderId: string,
    payerId?: string,
  ): Promise<PaymentCompletion> {
    console.log('✅ Completing server purchase:', { orderId, payerId })
    // Send payerId in the body as the backend expects
    const body = payerId ? { payerId, payer_id: payerId } : {}

    const response: AxiosResponse<PaymentCompletion> = await this.api.post(
      `/payment-flow/complete-server-purchase/${orderId}`,
      body,
    )
    console.log('🎉 Payment completion response:', response.data)
    return response.data
  }

  /**
   * Check payment status
   */
  async getPaymentStatus(orderId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      `/payment-flow/payment-status/${orderId}`,
    )
    return response.data
  }

  /**
   * Get server config file
   */
  async getServerConfigFile(serverId: string | number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(
      `/game-servers/${serverId}/config-file`,
    )
    return response.data
  }

  /**
   * Update server config file
   */
  async updateServerConfigFile(serverId: string | number, config: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(
      `/game-servers/${serverId}/config-file`,
      { config }
    )
    return response.data
  }

  /**
   * Update server settings
   */
  async updateServerSettings(serverId: string | number, settings: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.patch(
      `/game-servers/${serverId}/settings`,
      settings
    )
    return response.data
  }

  // =============================================================================
  // LEGACY COMPATIBILITY METHODS
  // These methods provide backward compatibility with the old multi-API service
  // =============================================================================

  /**
   * Get system metrics (for dashboard stats)
   */
  async getSystemMetrics(): Promise<SystemMetrics> {
    try {
      // Try to get real metrics from server (if endpoint exists)
      const response: AxiosResponse<SystemMetrics> = await this.api.get(
        '/game-servers/metrics',
      )
      return response.data
    } catch (error) {
      // Fallback: calculate basic metrics from server list
      const servers = await this.getGameServers()
      return {
        cpu_usage: 0,
        memory_usage: 0,
        disk_usage: 0,
        active_servers: servers.filter((s) => s.status === 'running').length,
        total_players: servers.reduce(
          (sum, s) => sum + (s.current_players || s.player_count || 0),
          0,
        ),
      }
    }
  }

  /**
   * Legacy method names for backward compatibility
   */
  async getGameServerConfig(serverId: string | number): Promise<any> {
    return this.getGameServer(serverId)
  }

  async updateGameServerConfig(
    serverId: string | number,
    config: any,
  ): Promise<{ message: string; restart_required: boolean }> {
    await this.updateGameServer(serverId, config)
    return {
      message: 'Configuration updated successfully',
      restart_required: false,
    }
  }

  async getGameServerStats(serverId: string | number): Promise<any> {
    return this.getGameServerStatus(serverId)
  }

  async getGameServerPlayers(serverId: string | number): Promise<any> {
    const status = await this.getGameServerStatus(serverId)
    return status.players || []
  }

  async executeGameServerRconCommand(
    serverId: string | number,
    command: string,
  ): Promise<any> {
    // This would need to be implemented in the Node.js server if RCON support is needed
    throw new Error('RCON commands not yet implemented in unified server')
  }

  async getGameServerRconPassword(serverId: string | number): Promise<{
    server_id: string | number
    rcon_password: string
    usage_info: string
  }> {
    // This would need to be implemented in the Node.js server if RCON support is needed
    throw new Error(
      'RCON password retrieval not yet implemented in unified server',
    )
  }

  // File management methods
  async getGameServerFiles(
    serverId: string | number,
    path: string = '',
  ): Promise<any[]> {
    const response: AxiosResponse<{ success: boolean; files: any[] }> = await this.api.get(
      `/game-servers/${serverId}/files`,
      { params: { path } }
    )
    return response.data.files || []
  }

  async getGameServerFileContent(
    serverId: string | number,
    filePath: string,
  ): Promise<{ path: string; content: string; editable: boolean }> {
    const response: AxiosResponse<{ success: boolean; path: string; content: string; editable: boolean }> =
      await this.api.post(`/game-servers/${serverId}/files/read`, { filePath })
    return {
      path: response.data.path,
      content: response.data.content,
      editable: response.data.editable
    }
  }

  async updateGameServerFile(
    serverId: string | number,
    filePath: string,
    content: string,
  ): Promise<{ message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> =
      await this.api.put(`/game-servers/${serverId}/files/update`, { filePath, content })
    return { message: response.data.message }
  }

  async deleteGameServerFile(
    serverId: string | number,
    filePath: string,
  ): Promise<{ message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> =
      await this.api.delete(`/game-servers/${serverId}/files/delete`, {
        data: { filePath }
      })
    return { message: response.data.message }
  }

  // Admin-only: read-only billing overview of all customer servers (paid/overdue/MRR).
  async getAdminBillingOverview(): Promise<AdminBillingOverview> {
    const response: AxiosResponse<AdminBillingOverview> = await this.api.get(
      '/game-servers/admin/billing-overview',
    )
    return response.data
  }

  async uploadGameServerFile(
    serverId: string | number,
    fileName: string,
    file: File,
    subdirectory?: string,
  ): Promise<{ message: string; jobId?: string }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fileName', fileName)
    formData.append('subdirectory', subdirectory || 'main')

    const response: AxiosResponse<{ success: boolean; message: string; jobId?: string }> =
      await this.api.post(`/game-servers/${serverId}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min timeout for HTTP upload to server
      })
    return { message: response.data.message, jobId: response.data.jobId }
  }

  async getUploadStatus(
    serverId: string | number,
    jobId: string,
  ): Promise<{
    jobId: string
    status: string
    fileName: string
    bytesTransferred: number
    totalBytes: number
    progress: number
    error?: string
    startedAt: number
  }> {
    const response = await this.api.get(
      `/game-servers/${serverId}/files/upload-status/${jobId}`
    )
    return response.data.upload
  }

  /**
   * Get CoD2x version information
   */
  async getCoD2xVersionInfo(serverId: string | number): Promise<{
    currentVersion: string
    latestVersion: string
    updateAvailable: boolean
    lastUpdate?: {
      version?: string
      timestamp?: string
      status?: string
      errorMessage?: string
    }
  }> {
    const response: AxiosResponse<{
      success: boolean
      currentVersion: string
      latestVersion: string
      updateAvailable: boolean
      lastUpdate?: {
        version?: string
        timestamp?: string
        status?: string
        errorMessage?: string
      }
    }> = await this.api.get(`/game-servers/${serverId}/cod2x/version`)
    return {
      currentVersion: response.data.currentVersion,
      latestVersion: response.data.latestVersion,
      updateAvailable: response.data.updateAvailable,
      lastUpdate: response.data.lastUpdate || undefined
    }
  }

  /**
   * Update CoD2x to latest version
   */
  async updateCoD2x(serverId: string | number): Promise<{
    success: boolean
    message: string
    version?: string
    lastUpdate?: {
      version?: string
      timestamp?: string
      status?: string
      errorMessage?: string
    }
  }> {
    const response: AxiosResponse<{
      success: boolean
      message: string
      error?: string
      version?: string
      lastUpdate?: {
        version?: string
        timestamp?: string
        status?: string
        errorMessage?: string
      }
    }> = await this.api.post(`/game-servers/${serverId}/cod2x/update`)
    return {
      success: response.data.success,
      message: response.data.message || response.data.error || 'Unknown error',
      version: response.data.version,
      lastUpdate: response.data.lastUpdate
    }
  }

  // =============================================================================
  // SFTP CREDENTIALS
  // =============================================================================

  /**
   * Check whether SFTP was already configured without loading the password.
   * Opening the SFTP tab must not create credentials or expose a saved secret.
   */
  async getSftpCredentialStatus(serverId: string | number): Promise<{
    configured: boolean
    host: string
    port: number
    username: string
  }> {
    const response: AxiosResponse<{
      success: boolean
      configured: boolean
      host: string
      port: number
      username: string
      error?: string
    }> = await this.api.get(`/game-servers/${serverId}/sftp-credentials/status`)
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch SFTP credential status')
    }
    return {
      configured: response.data.configured,
      host: response.data.host,
      port: response.data.port,
      username: response.data.username,
    }
  }

  /**
   * Fetch SFTP credentials for the owner's server.
   * Lazy-initialises the password on the backend if the customer hasn't
   * generated one yet.
   */
  async getSftpCredentials(serverId: string | number): Promise<{
    host: string
    port: number
    username: string
    password: string
  }> {
    const response: AxiosResponse<{
      success: boolean
      host: string
      port: number
      username: string
      password: string
      error?: string
    }> = await this.api.get(`/game-servers/${serverId}/sftp-credentials`)
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch SFTP credentials')
    }
    return {
      host: response.data.host,
      port: response.data.port,
      username: response.data.username,
      password: response.data.password,
    }
  }

  /**
   * Rotate the SFTP password. Rate-limited backend-side (5/hr).
   */
  async regenerateSftpCredentials(serverId: string | number): Promise<{
    host: string
    port: number
    username: string
    password: string
  }> {
    const response: AxiosResponse<{
      success: boolean
      host: string
      port: number
      username: string
      password: string
      error?: string
    }> = await this.api.post(`/game-servers/${serverId}/sftp-credentials/regenerate`)
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to rotate SFTP credentials')
    }
    return {
      host: response.data.host,
      port: response.data.port,
      username: response.data.username,
      password: response.data.password,
    }
  }

  // =============================================================================
  // INVOICE PAYMENT METHODS
  // =============================================================================

  /**
   * Get all user invoices
   */
  async getUserInvoices(serverId?: string): Promise<any[]> {
    const params = serverId ? { serverId } : {}
    const response: AxiosResponse<{ success: boolean; invoices: any[] }> =
      await this.api.get('/game-servers/invoices', { params })
    return response.data.invoices || []
  }

  /**
   * Get specific invoice details
   */
  async getInvoiceDetails(invoiceId: string): Promise<any> {
    const response: AxiosResponse<{ success: boolean; invoice: any }> =
      await this.api.get(`/game-servers/invoices/${invoiceId}`)
    return response.data.invoice
  }

  /**
   * Initiate manual payment for an invoice
   * @param invoiceId - The invoice ID to pay
   * @param durationMonths - Subscription duration (1, 3, or 6 months). 3 months = 10% off, 6 months = 15% off
   */
  async initiateInvoicePayment(
    invoiceId: string,
    durationMonths: 1 | 3 | 6 = 1
  ): Promise<{
    paymentId: string
    approvalUrl: string
    invoice: any
  }> {
    const response: AxiosResponse<{
      success: boolean
      paymentId: string
      approvalUrl: string
      invoice: any
    }> = await this.api.post(`/game-servers/invoices/${invoiceId}/pay`, {
      durationMonths,
    })
    return {
      paymentId: response.data.paymentId,
      approvalUrl: response.data.approvalUrl,
      invoice: response.data.invoice,
    }
  }

  /**
   * Update subscription settings (billing duration, auto-renew)
   * @param serverId - The server ID
   * @param settings - { preferredBillingDuration?: 1 | 3 | 6, autoRenew?: boolean }
   */
  async updateSubscriptionSettings(
    serverId: string,
    settings: { preferredBillingDuration?: 1 | 3 | 6; autoRenew?: boolean }
  ): Promise<{ success: boolean; server: any }> {
    const response: AxiosResponse<{
      success: boolean
      message: string
      server: any
    }> = await this.api.patch(`/game-servers/${serverId}/subscription-settings`, settings)
    return response.data
  }

  /**
   * Complete invoice payment after PayPal approval
   */
  async completeInvoicePayment(
    invoiceId: string,
    paymentId: string,
    payerId: string,
  ): Promise<{ success: boolean; message: string; invoice: any }> {
    const response: AxiosResponse<{
      success: boolean
      message: string
      invoice: any
    }> = await this.api.post(
      `/game-servers/invoices/${invoiceId}/complete-payment`,
      { paymentId, payerId },
    )
    return response.data
  }

  /**
   * Get payment status for an invoice
   */
  async getInvoicePaymentStatus(invoiceId: string): Promise<any> {
    const response: AxiosResponse<{ success: boolean; invoice: any; payment: any }> =
      await this.api.get(`/game-servers/invoices/${invoiceId}/payment-status`)
    return response.data
  }

  // =============================================================================
  // PAYPAL AUTO-PAY (SUBSCRIPTIONS)
  // =============================================================================

  /**
   * Get current auto-pay subscription state for a server.
   */
  async getServerSubscription(serverId: string): Promise<{
    success: boolean
    autoPayAvailable: boolean
    subscriptionStatus: 'none' | 'pending_approval' | 'active' | 'suspended' | 'cancelled' | 'expired'
    autoPayEnabled: boolean
    paypalSubscriptionId: string | null
    subscriptionDurationMonths: 1 | 3 | 6 | null
    subscriptionActivatedAt: string | null
    subscriptionCancelledAt: string | null
    nextPaymentDue: string | null
    paypalNextBillingTime?: string | null
    paypalLastPaymentTime?: string | null
    paypalStatus?: string
  }> {
    const response = await this.api.get(`/game-servers/${serverId}/subscription`)
    return response.data
  }

  /**
   * Initiate creating a PayPal subscription. Returns an approval URL the
   * customer must visit to authorize the recurring charge.
   */
  async createServerSubscription(
    serverId: string,
    durationMonths: 1 | 3 | 6
  ): Promise<{
    success: boolean
    subscriptionId: string
    approvalUrl: string
    planId: string
    durationMonths: 1 | 3 | 6
    cycleAmount: number
  }> {
    const response = await this.api.post(`/game-servers/${serverId}/subscription/create`, {
      durationMonths,
    })
    return response.data
  }

  /**
   * Confirm with the backend that PayPal approval completed and flip the
   * server's autoPayEnabled flag locally without waiting for the webhook.
   */
  async activateServerSubscription(
    serverId: string,
    subscriptionId: string
  ): Promise<{
    success: boolean
    subscriptionId: string
    subscriptionStatus: string
    autoPayEnabled: boolean
    nextBillingTime: string | null
  }> {
    const response = await this.api.post(`/game-servers/${serverId}/subscription/activate`, {
      subscriptionId,
    })
    return response.data
  }

  /**
   * Cancel auto-pay for a server.
   */
  async cancelServerSubscription(
    serverId: string,
    reason?: string
  ): Promise<{ success: boolean; subscriptionStatus: string; autoPayEnabled: boolean }> {
    const response = await this.api.post(`/game-servers/${serverId}/subscription/cancel`, {
      reason,
    })
    return response.data
  }

  // =============================================================================
  // ADMIN METHODS
  // =============================================================================

  /**
   * Admin: Create a server for another user (manual PayPal payment)
   */
  async adminCreateServerForUser(data: {
    userId: string
    serverConfig: {
      serverName?: string
      gameType: 'cod2x' | 'dod' | 'cs16' | 'cod4x' | 'ql'
      maxPlayers: number
      extraRamGb: number
      cpuPriority: 'normal' | 'realtime'
      visibility: 'private' | 'public'
      port?: number
      location?: 'chicago' | 'chi3x3d'
    }
    payment: {
      paypalTransactionId?: string
      amountPaid?: number
      durationType: 'subscription' | 'weekend_pass' | 'sponsored'
      durationMonths?: 1 | 3 | 6
    }
  }): Promise<{
    success: boolean
    message: string
    server: any
    invoice: any
    connection: { ip: string; port: number; rconPassword?: string }
  }> {
    const response: AxiosResponse<{
      success: boolean
      message: string
      server: any
      invoice: any
      connection: { ip: string; port: number; rconPassword?: string }
    }> = await this.api.post('/game-servers/admin/create-for-user', data)
    return response.data
  }

  /**
   * Debug method to check authentication
   */
  debugCheckToken(): void {
    console.log('🔍 Using unified Node.js server with Privy authentication')
    console.log('🔍 No separate token needed - using Privy getAccessToken()')
  }
}

const gameServerService = new GameServerService()

// Named export for use in index.ts
export { gameServerService }

// Make service available globally for debugging
if (typeof window !== 'undefined') {
  ;(window as any).gameServerService = gameServerService
  ;(window as any).unifiedGameServerApi = gameServerService
}

// Default export
export default gameServerService
